import type { LiveTruck, LiveTruckFix, PositionFeedEntry } from '@chukua/common';

const isNewer = (a: string, b: string): boolean => Date.parse(a) > Date.parse(b);

function fixOf({ source, lat, lng, speedMps, headingDeg, deviceTime }: PositionFeedEntry): LiveTruckFix {
  return { source, lat, lng, speedMps, headingDeg, deviceTime };
}

/**
 * Moves the trucks a stream message names to their new fix. A fix not newer than the one held is dropped (a snapshot fetched after a
 * stream message, or the reverse, can arrive in either order), and a truck the snapshot has not listed yet is left for the next
 * snapshot, which has the registration and trip the stream does not carry. Returns the same array when nothing changed, so a
 * message of stale fixes causes no render.
 */
export function applyPositionUpdate(trucks: LiveTruck[], updates: PositionFeedEntry[]): LiveTruck[] {
  const newest = new Map<string, PositionFeedEntry>();
  for (const update of updates) {
    const held = newest.get(update.truckId);
    if (!held || isNewer(update.deviceTime, held.deviceTime)) newest.set(update.truckId, update);
  }
  let changed = false;
  const next = trucks.map((truck) => {
    const update = newest.get(truck.truckId);
    if (!update || !isNewer(update.deviceTime, truck.fix.deviceTime)) return truck;
    changed = true;
    return { ...truck, fix: fixOf(update) };
  });
  return changed ? next : trucks;
}

/** A fresh snapshot, but never older than what the stream has already delivered for a truck. */
export function mergeSnapshot(current: LiveTruck[], snapshot: LiveTruck[]): LiveTruck[] {
  const held = new Map(current.map((truck) => [truck.truckId, truck]));
  return snapshot.map((truck) => {
    const streamed = held.get(truck.truckId);
    return streamed && isNewer(streamed.fix.deviceTime, truck.fix.deviceTime) ? { ...truck, fix: streamed.fix } : truck;
  });
}
